import React, { useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { DoubleSide, Group, MathUtils, Mesh } from 'three';
import { Text } from '@react-three/drei';

interface ThermometerProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  temperature?: number; // In Celsius
  isStirring?: boolean;
}

export function Thermometer({ 
  position = [0, 0, 0], 
  rotation = [0, 0, 0], 
  scale = 1, 
  temperature = 25,
  isStirring = false
}: ThermometerProps) {
  const innerGroupRef = useRef<Group>(null);
  const liquidScaleRef = useRef<Mesh>(null);
  const liquidPosRef = useRef<Mesh>(null);
  
  const [inspecting, setInspecting] = useState(false);
  const currentTempRef = useRef(temperature);
  
  // High fidelity realistic thermometer dimensions
  const glassRadius = 0.015;
  const height = 1.0; 
  const bulbRadius = 0.025;
  const innerRadius = 0.003; // Tiny capillary tube inside
  const glassThickness = 0.01;

  // Temperature mapping for 0 °C to 100 °C
  const minTemp = 0;
  const maxTemp = 100;
  
  // The reading scale visually starts somewhat above the bulb and ends near top
  const scaleStartY = -height / 2 + 0.15;
  const scaleEndY = height / 2 - 0.1;
  const scaleRange = scaleEndY - scaleStartY;

  const previousPointerRef = useRef({ x: 0, y: 0 });

  useFrame((state, delta) => {
    // Calculate simulated "shake" intensity based on mouse movement when inspecting
    const pointerDelta = Math.sqrt(
      Math.pow(state.pointer.x - previousPointerRef.current.x, 2) + 
      Math.pow(state.pointer.y - previousPointerRef.current.y, 2)
    );
    previousPointerRef.current = { x: state.pointer.x, y: state.pointer.y };
    
    // Normalized velocity
    const pointerVelocity = delta > 0 ? pointerDelta / delta : 0;

    let baseTransferRate = inspecting ? 0.015 : 1.5; // Very slow cooling in air, fast in liquid
    let shakeOffset = 0;

    if (inspecting) {
      // Vigorous shaking accelerates air convection cooling rapidly
      if (pointerVelocity > 0.5) {
        const shakeMult = Math.min(pointerVelocity * 0.15, 3.0); // Cap the max cooling factor
        baseTransferRate += shakeMult;
        
        // Add visual vibration
        shakeOffset = Math.sin(state.clock.getElapsedTime() * 40) * Math.min(pointerVelocity * 0.005, 0.05);
      }
    }

    // Physical Thermodynamics: Newton's law of cooling loosely applied.
    // If the thermometer is removed (inspected), it begins dropping towards room temp (25C).
    const targetTemp = inspecting ? 25 : temperature; 
    currentTempRef.current += (targetTemp - currentTempRef.current) * baseTransferRate * delta;

    // Update liquid visuals
    const mapped = Math.max(minTemp, Math.min(maxTemp, currentTempRef.current));
    const ratio = mapped / maxTemp;
    const lHeight = scaleStartY + ratio * scaleRange - (-height / 2);

    if (liquidScaleRef.current && liquidPosRef.current) {
      liquidScaleRef.current.scale.y = lHeight;
      liquidPosRef.current.position.y = -height / 2 + lHeight / 2;
    }

    // Handle interactive pop out to camera animations
    if (innerGroupRef.current) {
      const targetZ = inspecting ? 0.6 : 0;
      const targetY = inspecting ? 0.4 : 0;
      const targetRotX = inspecting ? -Math.PI / 8 : 0;
      const targetScale = inspecting ? 2.5 : 1;

      // Pop position out and up, using shake offset if being fanned manually
      innerGroupRef.current.position.lerp({ x: shakeOffset, y: targetY, z: targetZ } as any, 0.1);

      if (!inspecting && isStirring) {
        // Gentle manual stirring motion inside the tube
        const t = state.clock.getElapsedTime();
        innerGroupRef.current.rotation.x = Math.sin(t * 8) * 0.05;
        innerGroupRef.current.rotation.z = Math.cos(t * 8) * 0.05;
      } else {
        // Smoothly snap to target rotation
        innerGroupRef.current.rotation.x = MathUtils.lerp(innerGroupRef.current.rotation.x, targetRotX, 0.1);
        innerGroupRef.current.rotation.z = MathUtils.lerp(innerGroupRef.current.rotation.z, 0, 0.1);
      }

      // Smooth scaling effect when zooming in
      const curScale = MathUtils.lerp(innerGroupRef.current.scale.x, targetScale, 0.1);
      innerGroupRef.current.scale.set(curScale, curScale, curScale);
    }
  });

  const glassMaterial = (
    <meshPhysicalMaterial 
      color="#ffffff"
      transparent
      opacity={0.15}
      roughness={0.0}
      metalness={0.1}
      transmission={0.98}
      ior={1.5}
      clearcoat={1}
      clearcoatRoughness={0.0}
      thickness={0.01}
      side={DoubleSide}
      depthWrite={false}
    />
  );

  // Generate tick marks (1 degree intervals visually, label every 10)
  const ticks = [];
  for (let i = 0; i <= 100; i += 2) {
    const yPos = scaleStartY + (i / maxTemp) * scaleRange;
    const isMajor = i % 10 === 0;
    const tickWidth = isMajor ? 0.012 : 0.006;
    
    ticks.push(
      <group key={'tick-' + i}>
        <mesh position={[0, yPos, glassRadius + 0.0001]} renderOrder={2}>
          <planeGeometry args={[tickWidth, 0.0015]} />
          <meshBasicMaterial color="#222222" side={DoubleSide} />
        </mesh>
        {isMajor && (
          <Text
            position={[-0.015, yPos, glassRadius]}
            color="#000000"
            fontSize={0.018}
            fontWeight="bold"
            anchorX="right"
            anchorY="middle"
            renderOrder={2}
            material-depthTest={true}
            outlineWidth={0.001}
            outlineColor="#ffffff"
          >
            {i}
          </Text>
        )}
      </group>
    );
  }

  return (
    <group position={position} rotation={rotation} scale={scale}>
      <group ref={innerGroupRef} onClick={(e) => { e.stopPropagation(); setInspecting(!inspecting); }}>
        
        {/* Outer Glass Casing Tube */}
        <mesh position={[0, 0, 0]} renderOrder={2}>
          <cylinderGeometry args={[glassRadius, glassRadius, height, 16]} />
          {glassMaterial}
        </mesh>

        {/* Glass Top Dome */}
        <mesh position={[0, height / 2, 0]} renderOrder={2}>
          <sphereGeometry args={[glassRadius, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
          {glassMaterial}
        </mesh>

        {/* Glass Bulb at Bottom */}
        <mesh position={[0, -height / 2, 0]} renderOrder={2}>
          <sphereGeometry args={[bulbRadius, 32, 16]} />
          {glassMaterial}
        </mesh>

        {/* Red Dyed Alcohol Bulb */}
        <mesh position={[0, -height / 2, 0]} renderOrder={1}>
          <sphereGeometry args={[bulbRadius - 0.005, 32, 16]} />
          <meshPhysicalMaterial color="#cc0000" transmission={0.2} roughness={0.1} />
        </mesh>

        {/* Red Dyed Alcohol Capillary Column */}
        <group position={[0, 0, 0]}>
          <mesh ref={liquidPosRef} position={[0, -height / 2, 0]} renderOrder={1}>
            <mesh ref={liquidScaleRef} position={[0, 0, 0]} scale={[1, 1, 1]}>
              <cylinderGeometry args={[innerRadius, innerRadius, 1, 8]} />
              <meshBasicMaterial color="#cc0000" />
            </mesh>
          </mesh>
        </group>
        
        {/* Temperature scale markings */}
        <group position={[0, 0, 0]}>
          {/* Tick Lines */}
          {ticks}
          {/* White backing stripe for contrast reading the red liquid */}
          <mesh position={[0, -0.05, -0.007]} rotation={[0, 0, 0]} renderOrder={1}>
            <planeGeometry args={[0.016, height * 0.85]} />
            <meshBasicMaterial color="#ffffff" side={DoubleSide} />
          </mesh>
        </group>

      </group>
    </group>
  );
}
